import { useCallback, useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { SketchLayer, SketchTools } from "@/components/board/Sketch";
import { toast } from "sonner";
import { api, errMsg, uid } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { ErrorState, Loading } from "@/components/States";
import { CanvasItem, Connections, NOTE_COLORS } from "@/components/board/CanvasItem";
import { useBoard } from "@/components/board/useBoard";
import { MUSKOKA_ROOMS } from "@/lib/images";
import { uploadFile } from "@/components/project/PhotosTab";
import { copy } from "@/components/project/SettingsTab";
import AddItemDialog from "@/components/board/AddItemDialog";
import { ALIGN } from "@/components/board/AlignTools";
import BoardToolbar from "@/components/board/BoardToolbar";
import { ChangeOrderDialog } from "@/components/project/ChangeOrders";
import Presentation from "@/components/board/Presentation";
import ReactionBar, { ReactionChips } from "@/components/board/ReactionBar";
import ReimagineDialog from "@/components/board/ReimagineDialog";
import RoomHeader from "@/components/board/RoomHeader";
import RoomTabs from "@/components/board/RoomTabs";
import SidePanel from "@/components/board/SidePanel";
import VersionsDialog from "@/components/board/VersionsDialog";

const hit = (el, r) => el.x < r.x + r.w && el.x + el.w > r.x && el.y < r.y + r.h && el.y + el.h > r.y;

export default function BoardTab({ projectId }) {
  const { user, brand } = useAuth();
  const editable = user.role !== "client";
  const { board, setBoard, update, status, error, load, flush, undo, redo, canUndo, canRedo } = useBoard(projectId);
  const prefs = useFetch(`/projects/${projectId}/preferences`);
  const [room, setRoom] = useState(null);
  const [sketching, setSketching] = useState(false);
  const [ink, setInk] = useState({ tool: "pen", color: "#1f2428", width: 5 });
  const roomStrokes = (board?.sketches || []).filter((st) => st.room_id === room);
  const setRoomStrokes = (next) => update((b) => ({ ...b, sketches: [...(b.sketches || []).filter((st) => st.room_id !== room), ...next] }));
  const [sel, setSel] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [adding, setAdding] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const openEdit = (id) => { const el = board.elements.find((e) => e.id === id); if (!el) return; if (el.type === "note") return setEditingNote(id); if (["swatch", "palette", "heading", "dimension"].includes(el.type)) return; setEditItem(el); };
  const [connectFrom, setConnectFrom] = useState(undefined);
  const [panel, setPanel] = useState(true);
  const [present, setPresent] = useState(false);
  const [changeOrder, setChangeOrder] = useState(false);
  const [versions, setVersions] = useState(false);
  const [reimagine, setReimagine] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [marquee, setMarquee] = useState(null);
  const [zoom, setZoom] = useState(() => (window.innerWidth < 768 ? 0.6 : 1));
  const [counts, setCounts] = useState({});
  const [project, setProject] = useState(null);
  const scroller = useRef();
  const dragBase = useRef(null);
  const clipboard = useRef([]);
  const reactions = useFetch(board ? `/boards/${board.id}/reactions` : null);
  const selected = sel[sel.length - 1] || null;

  useEffect(() => { if (board && !board.rooms.find((r) => r.id === room)) setRoom(board.rooms[0]?.id); }, [board, room]);
  useEffect(() => { api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {}); }, [projectId]);

  const els = board ? board.elements.filter((e) => e.room_id === room) : [];
  const maxZ = els.reduce((m, e) => Math.max(m, e.z || 1), 1);

  const addElement = useCallback((partial, at) => {
    const sc = scroller.current;
    const pos = at || { x: (sc?.scrollLeft || 0) + 60 + Math.random() * 120, y: (sc?.scrollTop || 0) + 50 + Math.random() * 80 };
    const el = { room_id: room, z: maxZ + 1, ...pos, ...partial, id: uid() };
    update((b) => ({ ...b, elements: [...b.elements, el] }));
    setSel([el.id]);
    return el;
  }, [room, maxZ, update]);

  const patchMany = (fn, save = false, base = null) => update((b) => ({ ...b, elements: b.elements.map(fn) }), save, base);
  const onMove = (id, p) => {
    if (!dragBase.current) dragBase.current = board;
    const el = board.elements.find((e) => e.id === id);
    const ids = sel.includes(id) ? sel : [id];
    const dx = p.x !== undefined ? p.x - el.x : 0, dy = p.y !== undefined ? p.y - el.y : 0;
    patchMany((e) => (e.id === id ? { ...e, ...p } : ids.includes(e.id) && p.x !== undefined ? { ...e, x: Math.max(0, e.x + dx), y: Math.max(0, e.y + dy) } : e));
  };
  const commit = () => { update((b) => ({ ...b }), true, dragBase.current); dragBase.current = null; };
  const removeSel = () => {
    if (!sel.length) return;
    update((b) => ({ ...b, elements: b.elements.filter((e) => !sel.includes(e.id)), connections: b.connections.filter((c) => !sel.includes(c.from) && !sel.includes(c.to)) }));
    setSel([]);
  };
  const duplicate = () => {
    const copies = board.elements.filter((e) => sel.includes(e.id)).map((e) => ({ ...e, id: uid(), x: e.x + 30, y: e.y + 30, z: maxZ + 1, data: { ...e.data } }));
    if (!copies.length) return;
    update((b) => ({ ...b, elements: [...b.elements, ...copies] }));
    setSel(copies.map((c) => c.id));
  };
  const copySel = () => {
    const picked = board.elements.filter((e) => sel.includes(e.id)).map((e) => ({ ...e, data: { ...e.data } }));
    if (picked.length) { clipboard.current = picked; toast.message(`${picked.length} item${picked.length === 1 ? "" : "s"} copied`); }
  };
  const pasteClip = () => {
    if (!clipboard.current.length) return;
    let z = maxZ;
    const copies = clipboard.current.map((e) => ({ ...e, id: uid(), x: Math.max(0, (e.x || 40) + 40), y: Math.max(0, (e.y || 40) + 40), z: ++z, room_id: room, data: { ...e.data } }));
    update((b) => ({ ...b, elements: [...b.elements, ...copies] }));
    setSel(copies.map((c) => c.id));
    clipboard.current = copies.map((c) => ({ ...c, data: { ...c.data } }));
  };
  const align = (kind) => {
    const chosen = board.elements.filter((e) => sel.includes(e.id));
    const moved = Object.fromEntries(ALIGN[kind](chosen).map((e) => [e.id, e]));
    patchMany((e) => moved[e.id] || e, true);
  };

  const select = (id, shift = false) => {
    if (connectFrom !== undefined && editable) {
      if (connectFrom === null) { setConnectFrom(id); toast.message("Now pick the item to connect to"); return; }
      if (connectFrom !== id) update((b) => ({ ...b, connections: [...b.connections, { id: uid(), room_id: room, from: connectFrom, to: id }] }));
      setConnectFrom(undefined);
    }
    setSel((s) => (shift ? (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]) : s.includes(id) && s.length > 1 ? s : [id]));
  };

  const onKey = (e) => {
    if (!editable || editingNote || ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); return e.shiftKey ? redo() : undo(); }
    if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); return redo(); }
    if (mod && e.key.toLowerCase() === "a") { e.preventDefault(); return setSel(els.map((x) => x.id)); }
    if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); return duplicate(); }
    if (mod && e.key.toLowerCase() === "c") { if (sel.length) { e.preventDefault(); copySel(); } return; }
    if (mod && e.key.toLowerCase() === "v") { if (clipboard.current.length) { e.preventDefault(); pasteClip(); } return; }
    if (!sel.length) return;
    const step = e.shiftKey ? 40 : 10;
    const mv = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (mv) { e.preventDefault(); patchMany((x) => (sel.includes(x.id) ? { ...x, x: Math.max(0, x.x + mv[0]), y: Math.max(0, x.y + mv[1]) } : x), true); }
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeSel(); }
    if (e.key === "Escape") { setSel([]); setConnectFrom(undefined); }
  };

  const surfaceDown = (e) => {
    if (!(e.target === e.currentTarget || e.target.dataset.surface)) return;
    setEditingNote(null);
    if (!e.shiftKey) setSel([]);
    if (!editable) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setMarquee({ x0: (e.clientX - rect.left) / zoom, y0: (e.clientY - rect.top) / zoom, x1: null, y1: null, add: e.shiftKey });
  };
  const surfaceMove = (e) => {
    if (!marquee) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setMarquee({ ...marquee, x1: (e.clientX - rect.left) / zoom, y1: (e.clientY - rect.top) / zoom });
  };
  const surfaceUp = () => {
    if (marquee?.x1 !== null && marquee?.x1 !== undefined) {
      const r = { x: Math.min(marquee.x0, marquee.x1), y: Math.min(marquee.y0, marquee.y1), w: Math.abs(marquee.x1 - marquee.x0), h: Math.abs(marquee.y1 - marquee.y0) };
      if (r.w > 4 && r.h > 4) { const ids = els.filter((el) => hit(el, r)).map((el) => el.id); setSel((s) => (marquee.add ? [...new Set([...s, ...ids])] : ids)); }
    }
    setMarquee(null);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    if (!editable) return;
    const files = [...e.dataTransfer.files].filter((f) => f.type.startsWith("image/"));
    const rect = e.currentTarget.getBoundingClientRect();
    let x = (e.clientX - rect.left) / zoom, y = (e.clientY - rect.top) / zoom;
    for (const f of files) {
      try { const r = await uploadFile(f, projectId, "board"); addElement({ type: "image", w: 260, h: 240, data: { url: r.url, caption: "" } }, { x, y }); x += 24; y += 24; }
      catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
  };

  const share = async (on) => {
    try {
      if (on) { const { data } = await api.post(`/boards/${board.id}/share`); setBoard({ ...board, share_token: data.share_token }); copy(`${window.location.origin}/share/${data.share_token}`); }
      else { await api.delete(`/boards/${board.id}/share`); setBoard({ ...board, share_token: null }); toast.success("Public link turned off"); }
    } catch (err) { toast.error(errMsg(err)); }
  };

  const onCount = useCallback((list) => setCounts(list.reduce((m, c) => (c.element_id ? { ...m, [c.element_id]: (m[c.element_id] || 0) + 1 } : m), {})), []);

  const patchRoom = (p) => update((b) => ({ ...b, rooms: b.rooms.map((r) => (r.id === room ? { ...r, ...p } : r)) }));
  const setCover = async (file) => {
    try { const r = await uploadFile(file, projectId, "board"); patchRoom({ cover_url: r.url }); toast.success("Cover updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const renderRoom = async () => {
    if (els.length === 0) return toast.message("Add a few items to this room first");
    setRendering(true); await flush();
    try {
      const { data } = await api.post(`/boards/${board.id}/ai/render`, { room_id: room });
      addElement({ type: "image", w: 420, h: 330, data: { url: data.images[0].url, caption: `${board.rooms.find((r) => r.id === room)?.name} · AI concept rendering` } });
      toast.success("Concept rendering pinned to the board");
    } catch (e) { toast.error(errMsg(e)); } finally { setRendering(false); }
  };
  const narrate = async (roomId) => {
    await flush();
    const { data } = await api.post(`/boards/${board.id}/ai/narrate`, { room_id: roomId });
    setBoard({ ...board, rooms: board.rooms.map((r) => (r.id === roomId ? { ...r, narration: data.narration, narration_audio_url: null, narration_audio_for: null } : r)) });
    return data.narration;
  };
  const voice = async (roomId) => {
    const { data } = await api.post(`/boards/${board.id}/ai/narration-audio`, { room_id: roomId });
    const r0 = board.rooms.find((r) => r.id === roomId);
    setBoard({ ...board, rooms: board.rooms.map((r) => (r.id === roomId ? { ...r, narration_audio_url: data.url, narration_audio_for: r0.narration } : r)) });
    return data.url;
  };
  const react = async (value, note) => {
    try { await api.post(`/boards/${board.id}/reactions`, { element_id: selected, value, note }); reactions.reload(); toast.success("Thanks, the studio will see this"); } catch (e) { toast.error(errMsg(e)); }
  };
  const unreact = async () => {
    try { await api.delete(`/boards/${board.id}/reactions/${selected}`); reactions.reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!board) return <Loading rows={4} />;
  const selEl = board.elements.find((e) => e.id === selected);
  const activeRoom = board.rooms.find((r) => r.id === room);
  const canReimagine = sel.length === 1 && !!selEl && ["image", "furniture", "material"].includes(selEl.type) && !!selEl.data?.url?.startsWith("/api");
  const byEl = (reactions.data || []).reduce((m, r) => ({ ...m, [r.element_id]: [...(m[r.element_id] || []), r] }), {});
  const mine = selEl && (byEl[selEl.id] || []).find((r) => r.user_id === user.id);
  const mq = marquee && marquee.x1 !== null ? { left: Math.min(marquee.x0, marquee.x1), top: Math.min(marquee.y0, marquee.y1), width: Math.abs(marquee.x1 - marquee.x0), height: Math.abs(marquee.y1 - marquee.y0) } : null;

  return (
    <div className="space-y-4" onKeyDown={onKey}>
      <RoomTabs rooms={board.rooms} active={room} editable={editable} onSelect={(id) => { setRoom(id); setSel([]); }}
        onAdd={() => { const r = { id: uid(), name: MUSKOKA_ROOMS.find((n) => !board.rooms.some((x) => x.name === n)) || `Room ${board.rooms.length + 1}` }; update((b) => ({ ...b, rooms: [...b.rooms, r] })); setRoom(r.id); }}
        onRename={(id, name) => update((b) => ({ ...b, rooms: b.rooms.map((r) => (r.id === id ? { ...r, name } : r)) }))}
        onDelete={(id) => { if (window.confirm("Delete this room and everything on it?")) update((b) => ({ ...b, rooms: b.rooms.filter((r) => r.id !== id), elements: b.elements.filter((e) => e.room_id !== id), connections: b.connections.filter((c) => c.room_id !== id) })); }} />
      <BoardToolbar editable={editable} selected={!!selEl} selCount={sel.length} canReimagine={canReimagine} connecting={connectFrom !== undefined} status={status} shareToken={board.share_token} rendering={rendering}
        history={{ canUndo, canRedo, onUndo: undo, onRedo: redo }} onAlign={align}
        onAdd={setAdding} onReimagine={() => setReimagine(true)} onRender={renderRoom} onConnect={() => { setConnectFrom(connectFrom === undefined ? null : undefined); if (connectFrom === undefined) toast.message("Pick the first item to connect"); }}
        onDelete={removeSel} onFront={() => patchMany((e) => (sel.includes(e.id) ? { ...e, z: maxZ + 1 } : e), true)}
        onDuplicate={duplicate} onEditItem={selEl && !["note", "swatch", "palette", "heading", "dimension"].includes(selEl.type) ? () => openEdit(selEl.id) : null}
        onSketch={!sketching ? () => setSketching(true) : null} onChangeOrder={() => setChangeOrder(true)}
        onVersions={async () => { await flush(); setVersions(true); }} onPresent={async () => { await flush(); setPresent(true); }}
        onShare={() => share(true)} onUnshare={() => share(false)} onCopyShare={() => copy(`${window.location.origin}/share/${board.share_token}`)}
        onTogglePanel={() => setPanel(!panel)} />
      {activeRoom && <RoomHeader room={activeRoom} count={els.length} editable={editable} onStory={(story) => patchRoom({ story })} onCover={setCover} onFocus={(cover_focus) => patchRoom({ cover_focus })} />}
      <div className={`grid gap-4 ${panel ? "xl:grid-cols-[1fr_360px]" : ""}`}>
        <div className="relative min-w-0">
          <div ref={scroller} className="board-paper grain relative isolate h-[60vh] overflow-auto rounded-xl border shadow-inner md:h-[68vh]" data-testid="board-canvas">
            <div style={{ width: 2400 * zoom, height: 1600 * zoom }}>
            <div data-surface="1" data-testid="board-surface" className="relative origin-top-left" style={{ width: 2400, height: 1600, transform: `scale(${zoom})` }} onPointerDown={surfaceDown} onPointerMove={surfaceMove} onPointerUp={surfaceUp} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
              <Connections elements={els} connections={board.connections.filter((c) => c.room_id === room)} />
              <SketchLayer strokes={roomStrokes} roomId={room} active={editable && sketching} tool={ink.tool} color={ink.color} width={ink.width} zoom={zoom} onChange={setRoomStrokes} />
              {els.map((el) => (
                <CanvasItem key={el.id} el={el} editable={editable} selected={sel.includes(el.id)} editing={editingNote === el.id} commentCount={counts[el.id]} zoom={zoom}
                  onSelect={(id, shift) => select(id, shift)} onMove={onMove} onCommit={commit} onEdit={openEdit}
                  onText={(text) => { patchMany((e) => (e.id === el.id ? { ...e, data: { ...e.data, text } } : e), true); setEditingNote(null); }}>
                  <ReactionChips list={byEl[el.id]} />
                </CanvasItem>
              ))}
              {mq && <div className="pointer-events-none absolute z-[998] rounded border border-accent bg-secondary" style={mq} data-testid="marquee" />}
              {els.length === 0 && (
                <div className="pointer-events-none absolute left-10 top-10 max-w-sm" data-testid="board-empty-state">
                  <p className="font-serif text-3xl">An empty room</p>
                  <p className="mt-2 text-sm text-muted-foreground">{editable ? "Pin notes, swatches, palettes, materials and furniture from the toolbar, drag photos straight onto the board, or let the AI render the room once it has a few pieces." : "The studio hasn't added anything to this room yet."}</p>
                </div>
              )}
            </div>
            </div>
          </div>
          {editable && sketching && <SketchTools tool={ink.tool} setTool={(tool) => setInk({ ...ink, tool })} color={ink.color} setColor={(color) => setInk({ ...ink, color, tool: ink.tool === "eraser" ? "pen" : ink.tool })} width={ink.width} setWidth={(width) => setInk({ ...ink, width })} canUndo={roomStrokes.length > 0} onUndo={() => setRoomStrokes(roomStrokes.slice(0, -1))} onClear={() => setRoomStrokes([])} onDone={() => setSketching(false)} />}
          <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1 rounded-full border bg-card/90 p-1 shadow backdrop-blur" data-testid="zoom-controls">
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.3, +(z - 0.1).toFixed(2)))} data-testid="zoom-out"><ZoomOut className="h-4 w-4" /></Button>
            <button type="button" className="min-w-[44px] font-mono text-xs" onClick={() => setZoom(1)} data-testid="zoom-reset">{Math.round(zoom * 100)}%</button>
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} data-testid="zoom-in"><ZoomIn className="h-4 w-4" /></Button>
          </div>
          {!editable && selEl && <ReactionBar key={selEl.id} el={selEl} mine={mine} onReact={react} onClear={unreact} />}
        </div>
        {panel && (
          <aside className="h-[68vh] rounded-xl border bg-card p-3" data-testid="board-side-panel">
            <SidePanel boardId={board.id} selected={selected} roomId={room} canPin={editable} onCount={onCount} beforeSend={flush}
              onPin={(text) => { addElement({ type: "note", w: 240, h: 150, data: { text, color: NOTE_COLORS[1] } }); toast.success("Note pinned to the board"); }} />
          </aside>
        )}
      </div>
      {adding && <AddItemDialog type={adding} projectId={projectId} onSwitchType={setAdding} onClose={() => setAdding(null)} onUpload={(f) => uploadFile(f, projectId, "board").catch((e) => { toast.error(errMsg(e)); })}
        onAdd={(p) => { addElement(p); setAdding(null); }} />}
      {editItem && <AddItemDialog type={editItem.type} projectId={projectId} initial={editItem.data} onClose={() => setEditItem(null)} onUpload={(f) => uploadFile(f, projectId, "board").catch((e) => { toast.error(errMsg(e)); })}
        onAdd={(data) => { patchMany((e) => (e.id === editItem.id ? { ...e, data } : e), true); setEditItem(null); toast.success("Card updated"); }} />}
      <VersionsDialog open={versions} onClose={() => setVersions(false)} board={board} canEdit={editable} onRestored={setBoard} />
      {reimagine && selEl && <ReimagineDialog boardId={board.id} el={selEl} onClose={() => setReimagine(false)} onPin={(p) => { addElement(p, { x: selEl.x + 40, y: selEl.y + 40 }); toast.success("Pinned to the board"); }} />}
      {editable && <ChangeOrderDialog open={changeOrder} onOpenChange={setChangeOrder} projectId={project?.id} source="board" sourceRef={board.rooms.find((r) => r.id === room)?.name || ""} />}
      {present && <Presentation title={project?.name || "Presentation"} subtitle={project?.address || "Design presentation"} brandName={brand.name} rooms={board.rooms} elements={board.elements} connections={board.connections} sketches={board.sketches || []} startRoom={room} onClose={() => setPresent(false)} onNarrate={editable ? narrate : null} onVoice={editable ? voice : null} prefs={prefs.data} onRoomMode={editable ? (rid, mode) => update((b) => ({ ...b, rooms: b.rooms.map((r) => (r.id === rid ? { ...r, present_mode: mode } : r)) })) : null} />}
    </div>
  );
}
